import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { handleGetPopularChefWithDishes } from "../../../services/get_without_auth";
import isValidURL from "../../../ValidateUrl";
import { shefDetailPath } from "../../../shefPath";

import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

const MostLoveChef = () => {
  const [mostLoveChef, setMostLovedChef] = useState([]);
  const [activeChefIndex, setActiveChefIndex] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const city = JSON.parse(localStorage.getItem("region"));
        if (!city?.id) return;
        const lovedChef = await handleGetPopularChefWithDishes(city.id);

// Keep only active chefs and remove inactive chefs' dishes
const activeChefs = (lovedChef || [])
  .filter((chef) => chef.is_active === 1)
  .map((chef) => ({
    ...chef,
    menus: (chef.menus || []).filter(
      (dish) => dish?.user?.is_active === 1
    ),
  }));

setMostLovedChef(activeChefs);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const handleChefNav = (direction) => {
    if (direction === "next") {
      setActiveChefIndex((prev) => (prev + 1) % mostLoveChef.length);
    } else {
      setActiveChefIndex((prev) => (prev - 1 + mostLoveChef.length) % mostLoveChef.length);
    }
  };

  if (mostLoveChef.length < 1) return null;

  const activeChef = mostLoveChef[activeChefIndex];
  const chefImage = activeChef?.profile_pic && isValidURL(activeChef.profile_pic)
    ? activeChef.profile_pic
    : "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

  return (
    <section className="py-12 md:py-24 bg-gradient-to-br from-gray-100 via-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-20">
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-3 md:mb-4">
            Meet Our <span className="text-red-600">Star Shefs</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 font-light max-w-2xl mx-auto px-4">
            The culinary artists behind your favorite meals.
          </p>
          <div className="w-20 md:w-24 h-1.5 bg-gradient-to-r from-red-600 to-red-700 rounded-full mx-auto mt-4 md:mt-6" />
        </div>

        <div className="relative px-8 md:px-0">
          <AnimatePresence initial={false} mode="wait">
            <Link to={shefDetailPath(activeChef?.id, activeChef?.first_name, activeChef?.last_name)} className="block">
              <motion.div

              key={activeChefIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="group bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 shadow-xl md:shadow-2xl grid lg:grid-cols-2 gap-6 md:gap-12 items-center border border-gray-200 hover:border-red-200 relative overflow-hidden"
            >
              {/* Decorative elements - hidden on mobile for cleaner look */}
              <div className="hidden md:block absolute top-8 right-8 w-8 h-8 bg-gradient-to-r from-red-500/30 to-red-600/20 rounded-full animate-pulse"></div>
              <div className="hidden md:block absolute top-16 right-16 w-4 h-4 bg-red-400/40 rounded-full animate-pulse delay-500"></div>
              <div className="hidden md:block absolute bottom-8 left-8 w-6 h-6 bg-red-300/30 rounded-full animate-pulse delay-1000"></div>
              <div className="absolute top-0 left-0 w-full h-2 md:h-3 bg-gradient-to-r from-red-600 to-red-700 opacity-50 md:opacity-20 md:group-hover:opacity-70 transition-opacity duration-300"></div>

              {/* Mobile: Image first, Desktop: Content first */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { delay: 0.2, duration: 0.5 } }}
                className="w-full h-64 md:h-80 lg:h-96 rounded-2xl md:rounded-3xl overflow-hidden shadow-lg md:shadow-2xl relative border-2 md:border-4 border-white order-first lg:order-last"
              >
                <div className="hidden md:block absolute top-4 left-4 w-4 h-4 bg-white/70 rounded-full z-10 animate-pulse"></div>
                <img
                  src={chefImage}
                  alt={activeChef?.first_name}
                  className="w-full h-full object-cover object-top"
                />
              </motion.div>

              <div className="space-y-4 md:space-y-6 relative z-10">
                <div>
                  <h3 className="text-2xl md:text-4xl font-black tracking-tight mb-2">
                    {activeChef?.first_name} {activeChef?.last_name}
                  </h3>
                  <p className="text-red-600 font-bold text-lg md:text-xl mb-3 md:mb-4">Top Rated Chef</p>
                  <div className="w-12 md:w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full"></div>
                </div>

                <div className="flex items-center space-x-2 md:space-x-3 bg-green-50 rounded-full px-3 md:px-4 py-1.5 md:py-2 w-fit border border-green-200">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                  <span className="text-green-700 font-semibold text-sm md:text-base">Verified Top Chef</span>
                </div>

                <p className="text-gray-600 leading-relaxed text-sm md:text-lg font-light line-clamp-3 md:line-clamp-none">
                  {activeChef?.bio}
                </p>

                <div>
                  <h4 className="font-bold text-xl md:text-2xl mb-4 md:mb-6 text-gray-900">Signature Dishes</h4>
                  <div className="md:grid md:grid-cols-2 md:gap-6 flex gap-3 overflow-x-auto md:overflow-visible pb-2 -mx-1 px-1">
                    {activeChef?.menus?.slice(0, 4).map((dish) => (
                      <Link
                        to={`/dish-detail-single/${dish.id}`}
                        onClick={(e) => e.stopPropagation()}
                        key={dish.id}
                        className="group min-w-[140px] md:min-w-0 
                        bg-gradient-to-br from-gray-50 to-gray-100 
                        rounded-lg md:rounded-2xl 
                        p-2 md:p-4 
                        hover:bg-white hover:shadow-lg transition-all duration-300 
                        relative overflow-hidden border border-gray-200"
                      >
                        <div className="hidden md:block absolute top-2 right-2 w-3 h-3 bg-red-500/30 rounded-full animate-pulse"></div>
                          <div className="h-20 md:h-32 rounded-lg md:rounded-xl overflow-hidden mb-2 md:mb-3">
                          <img
                            src={dish.logo && isValidURL(dish.logo) ? dish.logo : "/placeholder.svg"}
                            alt={dish.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <h5 className="font-semibold text-gray-900 mb-0.5 text-xs md:text-base line-clamp-1">{dish.name}</h5>
                        <p className="text-red-600 font-bold text-sm md:text-lg">
                          {`${(dish.chef_earning_fee +
                        dish.platform_price +
                        dish.delivery_price).toLocaleString("en-PK", {style: "currency", currency: "PKR"})}`}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              </motion.div>
            </Link>
          </AnimatePresence>


          {/* Navigation Buttons - Better positioned for mobile */}
          <button
            onClick={() => handleChefNav("prev")}
            className="absolute top-1/2 -left-4 md:-left-6 -translate-y-1/2 bg-white rounded-full p-2 md:p-4 shadow-lg md:shadow-xl hover:bg-red-600 hover:text-white transition-all duration-300 border border-gray-200 hover:border-red-500 z-20"
            aria-label="Previous chef"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={() => handleChefNav("next")}
            className="absolute top-1/2 -right-4 md:-right-6 -translate-y-1/2 bg-white rounded-full p-2 md:p-4 shadow-lg md:shadow-xl hover:bg-red-600 hover:text-white transition-all duration-300 border border-gray-200 hover:border-red-500 z-20"
            aria-label="Next chef"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Mobile pagination dots */}
          <div className="flex justify-center mt-6 md:hidden space-x-2">
            {mostLoveChef.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveChefIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeChefIndex ? "w-6 bg-red-600" : "w-2 bg-gray-300"
                }`}
                aria-label={`Go to chef ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MostLoveChef;